import { Node, Edge }                                 from "@xyflow/react";
import { ServiceNodeData }                            from "features/workflow/CustomNode";
import { RunContextApp }                              from "framework/RCApp";
import { CMSApiResponse }                          from "framework/http/CMSApiClient";
import { APIData, 
         APIResponseCollection, 
         GetValues 
       }                                              from "types/cms/frontendTypes";

export interface CreateSystemNodeResponse {
  data          : APIData<"api::system-node.system-node">
  nodePosition  : APIData<"api::system-node-position.system-node-position">
}

export interface NodesResponse {
  nodes : APIData<"api::system-node.system-node">[]
}

export const WorkflowAPI = {

  getWorkflows : async (rc: RunContextApp) 
      : Promise<CMSApiResponse<APIResponseCollection<"api::feature-workflow.feature-workflow">>> => {

    if (!rc.permissionsManager.canFeatureWorkflowFind()) {
      return {
        data : {
          data : [],
        } as any
      }
    }

    const resp = await rc.cmsApiClient.getClient().collection("feature-workflows").find({
      populate : {
        systemNodeEdges : {
          fields : '*'
        },
        systemNodePositions : {
          fields : ['documentId']
        }
      }
    })
    return resp as unknown as CMSApiResponse<APIResponseCollection<"api::feature-workflow.feature-workflow">>
  },

  getNodes : async (rc: RunContextApp) 
      : Promise<CMSApiResponse<APIResponseCollection<"api::system-node.system-node">>> => {

    if (!rc.permissionsManager.canSystemNodeFind()) {
      return {
        data : {
          data : [],
        } as any
      }
    }

    const systemNodesCollection = rc.cmsApiClient.getClient().collection("system-nodes")
    const resp  = await systemNodesCollection.find({
      populate : [
        'configs'
      ],
    })

    return resp as unknown as CMSApiResponse<APIResponseCollection<"api::system-node.system-node">>
  },

  getNodePositions : async (rc: RunContextApp) 
      : Promise<CMSApiResponse<APIResponseCollection<"api::system-node-position.system-node-position">>> => {

    if (!rc.permissionsManager.canSystemNodePositionFind()) {
      return {
        data : {
          data : [],
        } as any
      }
    }
    
    const systemNodePositionsCollection = rc.cmsApiClient.getClient().collection("system-node-positions")
    const resp = await systemNodePositionsCollection.find({
      populate : {
        featureWorkflow : {
          fields : ['documentId']
        },
        systemNode : {
          fields : ['documentId']
        }
      }
    })

    return resp as unknown as CMSApiResponse<APIResponseCollection<"api::system-node-position.system-node-position">> 
  },

  createSystemNode : async (rc: RunContextApp, 
                            workflowDocId : string,
                            node : Node<ServiceNodeData>) 
      : Promise<CMSApiResponse<CreateSystemNodeResponse>> => {

    const systemNodeInfo = WorkflowAPI.convertNodeToSystemNodeAndPosition(node, workflowDocId)
    const systemNodesCollection = rc.cmsApiClient.getClient().collection("system-nodes")
    const resp = await systemNodesCollection.create(systemNodeInfo)

    return resp as unknown as CMSApiResponse<CreateSystemNodeResponse>
  },

  updateSystemNode : async (rc: RunContextApp, sysNodeDocId : string, 
                            sysNodeData : GetValues<"api::system-node.system-node">) 
      : Promise<CMSApiResponse<APIData<"api::system-node.system-node">>> => {

    const systemNodesCollection = rc.cmsApiClient.getClient().collection("system-nodes")
    const resp = await systemNodesCollection.update(sysNodeDocId, sysNodeData, {
      populate : [
        'configs'
      ],
    })

    return resp as unknown as CMSApiResponse<APIData<"api::system-node.system-node">>
  },
  
  deleteSystemNode : async (rc: RunContextApp, systemNode: any) : Promise<any> => {

    const systemNodesCollection = rc.cmsApiClient.getClient().collection("system-nodes")
    const resp = await systemNodesCollection.delete(systemNode.id)
    return resp
  }, 

  addNodePosition : async (rc: RunContextApp, nodePosition : GetValues<"api::system-node-position.system-node-position">) 
      : Promise<CMSApiResponse<APIData<"api::system-node-position.system-node-position">>> => {

    const systemNodePositionsCollection = rc.cmsApiClient.getClient().collection("system-node-positions")
    const resp = await systemNodePositionsCollection.create(nodePosition, {
      populate : {
        featureWorkflow : {
          fields : ['documentId']
        },
        systemNode : {
          fields : ['documentId']
        }
      }
    })
    return resp as unknown as CMSApiResponse<APIData<"api::system-node-position.system-node-position">>
  },

  updateNodePosition : async (rc: RunContextApp, nodePosDocId : string, 
                              nodePosition : GetValues<"api::system-node-position.system-node-position">) 
      : Promise<CMSApiResponse<APIData<"api::system-node-position.system-node-position">>> => {

    const systemNodePositionsCollection = rc.cmsApiClient.getClient().collection("system-node-positions")
    const resp = await systemNodePositionsCollection.update(nodePosDocId, nodePosition, {
      populate : {
        featureWorkflow : {
          fields : ['documentId']
        },
        systemNode : {
          fields : ['documentId']
        }
      }
    })
    return resp as unknown as CMSApiResponse<APIData<"api::system-node-position.system-node-position">>
  },
  
  deleteNodePosition : async (rc: RunContextApp, nodePositionDocId : string) 
      : Promise<CMSApiResponse<any>> => {

    const systemNodePositionsCollection = rc.cmsApiClient.getClient().collection("system-node-positions")
    const resp = await systemNodePositionsCollection.delete(nodePositionDocId)
    return resp as any
  },

  createSystemNodeEdge : async (rc: RunContextApp, edge : Edge, workflowDocId : string) 
      : Promise<CMSApiResponse<APIData<"api::system-node-edge.system-node-edge">>> => {

    const systemNodeEdge = WorkflowAPI.convertEdgeToSystemNodeEdge(edge, workflowDocId)
    const systemNodeEdgesCollection = rc.cmsApiClient.getClient().collection("system-node-edges")
    const resp = await systemNodeEdgesCollection.create(systemNodeEdge)
    return resp as unknown as CMSApiResponse<APIData<"api::system-node-edge.system-node-edge">>
  },

  deleteSystemNodeEdge : async (rc: RunContextApp, edgeDocId : string) : Promise<any> => {

    const systemNodeEdgesCollection = rc.cmsApiClient.getClient().collection("system-node-edges")
    const resp = await systemNodeEdgesCollection.delete(edgeDocId)
    return resp
  },

  convertNodeToSystemNodeAndPosition : (node: Node<ServiceNodeData>, workflowDocId : string) => {
    
    return {
      sysNodePos : {
        workflowDocId : workflowDocId,
        positionX : node.position.x,
        positionY : node.position.y
      },
      ...node.data
    }
  },

  convertEdgeToSystemNodeEdge : (edge: Edge, workflowDocId : string) => {

    return {
      source : edge.source,
      target : edge.target,
      label : edge.label,
      type : edge.type,
      sourceHandle : edge.sourceHandle,
      targetHandle : edge.targetHandle,
      featureWorkflow : {
        connect: [{ documentId: workflowDocId }]
      }
    }
  },
}